import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/shared/EmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { PlatformQuoteIntake } from "@/services/platformQuoteRequestsService";

export interface PlatformQuoteIntakeTranscriptProps {
  intake: PlatformQuoteIntake;
}

/**
 * The intake conversation, shown to the admin routing a request that triage
 * could not place (spec add326e0 §4d.2, acceptance 6).
 *
 * This is the difference between routing as a fresh judgement from a one-line
 * description and routing as a confirm. The transcript says what the consumer
 * actually described and what the model asked; the suggestion block says where
 * the model was leaning and how sure it was. An admin who agrees clicks the
 * suggestion; one who does not can see why before overriding it.
 */
export function PlatformQuoteIntakeTranscript({
  intake,
}: PlatformQuoteIntakeTranscriptProps): JSX.Element {
  const { t } = useTranslation();
  const draft = intake.draft;
  const confidencePct =
    typeof draft?.confidence === "number" ? Math.round(draft.confidence * 100) : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("platform.quoteRequestDetail.intakeTitle")}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("platform.quoteRequestDetail.intakeHint")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {draft?.discipline || draft?.rationale ? (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="font-medium text-foreground">
              {t("platform.quoteRequestDetail.intakeSuggestion", {
                discipline: disciplineLabel(draft.discipline),
                extra: draft.alsoNeeds ? ` + ${disciplineLabel(draft.alsoNeeds)}` : "",
              })}
              {confidencePct != null ? ` · ${confidencePct}%` : ""}
            </p>
            {draft.rationale ? (
              <p className="mt-1 text-muted-foreground">{draft.rationale}</p>
            ) : null}
          </div>
        ) : null}

        {intake.messages.length === 0 ? (
          <EmptyState
            title={t("platform.quoteRequestDetail.intakeEmptyTitle")}
            message={t("platform.quoteRequestDetail.intakeEmpty")}
            className="py-6"
          />
        ) : (
          /* The border and padding are not decoration: the scroller clips the
             last bubble mid-sentence, and with no edge to clip AGAINST that
             reads as a broken card rather than as more content below. */
          <div className="max-h-96 space-y-2 overflow-y-auto rounded-md border border-border p-3">
            {intake.messages.map((message, index) => (
              <div
                key={`${message.at}-${index}`}
                className={cn("flex", message.role === "consumer" ? "justify-end" : "justify-start")}
              >
                <p
                  className={cn(
                    /* 85% of a full-width admin card is a ~1500px line on a
                       wide monitor, which is unreadable as prose. The ch cap
                       holds the measure at a normal reading width and the
                       percentage still governs on narrow screens. */
                    "max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm md:max-w-[70ch]",
                    message.role === "consumer"
                      ? "rounded-br-md bg-primary text-primary-foreground"
                      : "rounded-tl-md border border-border bg-card text-foreground",
                  )}
                >
                  {message.content}
                </p>
              </div>
            ))}
          </div>
        )}

      </CardContent>
    </Card>
  );
}

function disciplineLabel(discipline: string | null | undefined): string {
  if (discipline === "notary") return "Escribano";
  if (discipline === "accountant") return "Contador";
  return "—";
}
